/**
 * GlobalTransportPage
 * 글로벌 운송 관련 서브 페이지 전용 클래스
 */
export class GlobalTransportPage {
    constructor() {
        this.section = null;
        this.nav = null;
        this.navWrap = null;
        this.navItems = [];
        this.contentItems = [];
        this.activeIndex = 0;
        this.processSwiper = null;
        this.lastScrollY = 0; // 스크롤 방향 감지용
        this.scrollDirection = "down"; // "up" or "down"
    }

    /**
     * 국제 운송 서비스 페이지 초기화
     */
    internationalTransportService() {
        this.init();
    }

    /**
     * W&D 통합 서비스 페이지 초기화
     */
    wdIntegratedService() {
        this.init();
    }

    init() {
        this.section = document.getElementById("transport-module-section");
        if (!this.section) return;

        this.nav = this.section.querySelector(".no-sub-module__nav");
        this.navWrap = this.ensureNavWrap();
        this.navItems = Array.from(
            this.section.querySelectorAll(".no-sub-module__item"),
        );
        this.contentItems = Array.from(
            this.section.querySelectorAll(".no-sub-module__content"),
        );

        if (this.contentItems.length === 0) return;

        // If older pin-based code ran, clean it up first.
        this.cleanupLegacyPin();

        // 모바일 체크
        const isMobile = window.matchMedia("(max-width: 1024px)").matches;

        // Swiper 초기화 (운송 프로세스)
        this.initSwiper();

        // PC에서만 네비게이션 클릭 이벤트 설정
        if (!isMobile && this.navItems.length > 0) {
            this.setupNavEvents();
        }

        // Scroll 기반 active 처리 + pinned 상태 토글 (ScrollTrigger pin 없이)
        this.setupScrollBasedActive();
        this.setupPinnedNav();
    }

    ensureNavWrap() {
        if (!this.nav) return null;
        const parent = this.nav.parentElement;
        if (parent && parent.classList.contains("no-sub-module__nav-wrap")) {
            return parent;
        }

        const wrap = document.createElement("div");
        wrap.className = "no-sub-module__nav-wrap";
        if (parent) parent.insertBefore(wrap, this.nav);
        wrap.appendChild(this.nav);
        return wrap;
    }

    initSwiper() {
        const swiperEl = this.section.querySelector(
            "#transport-process-swiper",
        );
        if (!swiperEl || typeof Swiper === "undefined") return;

        // Swiper 초기화 전에 컨테이너 크기 확인
        const updateSwiper = () => {
            if (this.processSwiper) {
                this.processSwiper.destroy(true, true);
            }

            // 컨테이너 너비 강제 설정
            const containerWidth = swiperEl.offsetWidth || swiperEl.clientWidth;
            if (containerWidth > 0) {
                swiperEl.style.width = `${containerWidth}px`;
            }

            this.processSwiper = new Swiper(swiperEl, {
                slidesPerView: 2.5,
                spaceBetween: 24,
                autoHeight: false,
                watchOverflow: true,
                centeredSlides: false,
                loop: false,
                scrollbar: {
                    el: ".no-sub-module__scrollbar",
                    draggable: true,
                },
                navigation: {
                    nextEl: ".no-sub-module__swiper-next",
                    prevEl: ".no-sub-module__swiper-prev",
                },
                breakpoints: {
                    320: {
                        slidesPerView: 1.5,
                        spaceBetween: 16,
                    },
                    768: {
                        slidesPerView: 2.5,
                        spaceBetween: 24,
                    },
                    1024: {
                        slidesPerView: 2.5,
                        spaceBetween: 32,
                    },
                },
                on: {
                    init: function () {
                        // Swiper 초기화 후 크기 재계산
                        this.update();
                        this.updateSlides();
                    },
                },
            });
        };

        // 이미지 로드 완료 후 Swiper 초기화
        const images = swiperEl.querySelectorAll("img");
        if (images.length > 0) {
            let loadedCount = 0;
            const totalImages = images.length;

            images.forEach((img) => {
                if (img.complete) {
                    loadedCount++;
                } else {
                    img.addEventListener("load", () => {
                        loadedCount++;
                        if (loadedCount === totalImages) {
                            setTimeout(updateSwiper, 100);
                        }
                    });
                    img.addEventListener("error", () => {
                        loadedCount++;
                        if (loadedCount === totalImages) {
                            setTimeout(updateSwiper, 100);
                        }
                    });
                }
            });

            if (loadedCount === totalImages) {
                updateSwiper();
            } else {
                // 일부 이미지가 로드되지 않았어도 일정 시간 후 초기화
                setTimeout(updateSwiper, 500);
            }
        } else {
            updateSwiper();
        }
    }

    setupNavEvents() {
        this.navItems.forEach((item, index) => {
            const btn = item.querySelector(".no-sub-module__btn");
            if (!btn) return;

            btn.addEventListener("click", () => {
                this.setActiveIndex(index);
                this.scrollToContent(index);
            });
        });
    }

    cleanupLegacyPin() {
        if (!this.nav) return;
        if (typeof ScrollTrigger === "undefined" || !ScrollTrigger.getAll) return;

        ScrollTrigger.getAll().forEach((t) => {
            try {
                if (t && t.vars && t.vars.pin === this.nav) {
                    t.kill(true);
                }
            } catch (e) {
                // ignore
            }
        });
    }

    setupPinnedNav() {
        if (!this.section || !this.nav || !this.navWrap) return;

        const mq = window.matchMedia("(min-width: 1025px)");
        let rafId = null;

        const getTopOffset = () => {
            const headerH = document.getElementById("main-header")?.offsetHeight || 0;

            // sub-nav may be fixed under the header, so include it.
            const subNav = document.querySelector(".no-sub-nav.--fixed") || document.querySelector(".no-sub-nav");
            const subNavH = subNav ? subNav.offsetHeight || 0 : 0;

            // Small breathing room so it doesn't feel glued.
            return headerH + subNavH + 8;
        };

        const update = () => {
            if (!mq.matches) {
                this.nav.classList.remove("--pinned");
                this.nav.style.position = "";
                this.nav.style.top = "";
                this.nav.style.left = "";
                this.nav.style.width = "";
                return;
            }

            const rect = this.section.getBoundingClientRect();
            const wrapRect = this.navWrap.getBoundingClientRect();
            const navHeight = this.nav.offsetHeight || 0;
            const shouldPin = rect.top <= 0 && rect.bottom > navHeight;

            if (shouldPin) {
                this.nav.classList.add("--pinned");
                this.nav.style.position = "fixed";
                this.nav.style.top = `${getTopOffset()}px`;
                this.nav.style.left = `${wrapRect.left}px`;
                this.nav.style.width = `${wrapRect.width}px`;
            } else {
                this.nav.classList.remove("--pinned");
                this.nav.style.position = "";
                this.nav.style.top = "";
                this.nav.style.left = "";
                this.nav.style.width = "";
            }
        };

        const scheduleUpdate = () => {
            if (rafId) return;
            rafId = requestAnimationFrame(() => {
                update();
                rafId = null;
            });
        };

        window.addEventListener("scroll", scheduleUpdate, { passive: true });
        window.addEventListener("resize", scheduleUpdate, { passive: true });

        if (typeof mq.addEventListener === "function") {
            mq.addEventListener("change", scheduleUpdate);
        } else if (typeof mq.addListener === "function") {
            mq.addListener(scheduleUpdate);
        }

        // 초기 상태
        setTimeout(update, 0);
    }

    setupScrollBasedActive() {
        // 스크롤 방향 감지
        const updateScrollDirection = () => {
            const currentScrollY = window.pageYOffset || window.scrollY;
            this.scrollDirection =
                currentScrollY > this.lastScrollY ? "down" : "up";
            this.lastScrollY = currentScrollY;
        };

        // active 상태를 결정하는 함수
        const determineActiveIndex = () => {
            const viewportHeight = window.innerHeight;
            const viewportTop = window.pageYOffset;
            const viewportBottom = viewportTop + viewportHeight;

            let activeIndex = 0;
            let maxScore = 0;

            this.contentItems.forEach((item, i) => {
                const rect = item.getBoundingClientRect();
                const itemTop = rect.top + window.pageYOffset;
                const itemBottom = itemTop + rect.height;

                let score = 0;

                if (this.scrollDirection === "down") {
                    // 아래로 스크롤: content의 top이 뷰포트 상단 근처에 오면 active
                    // 뷰포트 상단에서 30% 지점을 트리거 포인트로 사용
                    const triggerPoint = viewportTop + viewportHeight * 0.3;

                    if (itemTop <= triggerPoint && itemBottom > triggerPoint) {
                        // content의 top이 트리거 포인트를 지나갔을 때
                        score = 100;
                    } else {
                        // content가 뷰포트에 보이는 정도
                        const visibleTop = Math.max(rect.top, 0);
                        const visibleBottom = Math.min(
                            rect.bottom,
                            viewportHeight,
                        );
                        const visibleHeight = Math.max(
                            0,
                            visibleBottom - visibleTop,
                        );
                        score = (visibleHeight / viewportHeight) * 50;
                    }
                } else {
                    // 위로 스크롤: content의 bottom이 뷰포트 하단 근처에 오면 active
                    // 뷰포트 하단에서 30% 지점을 트리거 포인트로 사용
                    const triggerPoint = viewportBottom - viewportHeight * 0.3;

                    if (itemTop < triggerPoint && itemBottom >= triggerPoint) {
                        // content의 bottom이 트리거 포인트를 지나갔을 때
                        score = 100;
                    } else {
                        // content가 뷰포트에 보이는 정도
                        const visibleTop = Math.max(rect.top, 0);
                        const visibleBottom = Math.min(
                            rect.bottom,
                            viewportHeight,
                        );
                        const visibleHeight = Math.max(
                            0,
                            visibleBottom - visibleTop,
                        );
                        score = (visibleHeight / viewportHeight) * 50;
                    }
                }

                if (score > maxScore) {
                    maxScore = score;
                    activeIndex = i;
                }
            });

            return activeIndex;
        };

        // 스크롤 이벤트로 active 상태 업데이트 (실시간)
        const handleScroll = () => {
            updateScrollDirection();

            // 섹션이 뷰포트에 있는지 확인
            const sectionRect = this.section.getBoundingClientRect();
            if (
                sectionRect.bottom < 0 ||
                sectionRect.top > window.innerHeight
            ) {
                return;
            }

            const activeIndex = determineActiveIndex();
            this.setActiveIndex(activeIndex);
        };

        // requestAnimationFrame을 사용하여 부드러운 업데이트
        let rafId = null;
        const rafHandleScroll = () => {
            handleScroll();
            rafId = null;
        };

        window.addEventListener(
            "scroll",
            () => {
                if (!rafId) {
                    rafId = requestAnimationFrame(rafHandleScroll);
                }
            },
            { passive: true },
        );

        // 초기 체크
        setTimeout(() => {
            const activeIndex = determineActiveIndex();
            this.setActiveIndex(activeIndex);
        }, 100);
    }

    setActiveIndex(index) {
        if (index < 0 || index >= this.contentItems.length) return;

        // 모바일 체크
        const isMobile = window.matchMedia("(max-width: 1024px)").matches;

        // 이미 같은 인덱스면 중복 처리 방지
        if (index === this.activeIndex) return;

        this.activeIndex = index;

        // PC에서만 네비게이션 활성화 변경
        if (!isMobile && this.navItems.length > 0) {
            this.navItems.forEach((item, i) => {
                if (i === index) {
                    item.classList.add("--active");
                } else {
                    item.classList.remove("--active");
                }
            });
        }
    }

    scrollToContent(index) {
        const content = this.contentItems[index];
        if (!content) return;

        // 섹션 기준으로 스크롤 위치 계산
        const sectionTop =
            this.section.getBoundingClientRect().top + window.pageYOffset;
        const contentTop =
            content.getBoundingClientRect().top + window.pageYOffset;
        const offsetTop = contentTop - sectionTop - 200;

        window.scrollTo({
            top: sectionTop + offsetTop,
            behavior: "smooth",
        });
    }
}
